import { AsyncPipe, NgIf, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TimezoneService } from '@sc/data-access-configuration';
import {
  Appointment,
  Fitter,
  RequestAppointment,
} from '@sc/data-access-models';
import { FittersSandboxService } from '@sc/data-access-state-management';
import { LocaleDatePipe } from '@sc/data-access-translation';
import {
  FitterNameDisplayService,
  FitterNamePipe,
} from '@sc/feature-contextual-display';
import { IconComponent, MomentParseZonePipe } from '@sc/ui-components';

@Component({
  selector: 'sc-declaration-end-history-appointment',
  styleUrls: ['./declaration-end-history-appointment.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FitterNameDisplayService],
  imports: [
    NgIf,
    IconComponent,
    AsyncPipe,
    TitleCasePipe,
    MomentParseZonePipe,
    TranslateModule,
    LocaleDatePipe,
    FitterNamePipe,
  ],
  template: `
    <div [class]="'info-appointment ' + getTypeAppointmentClass()">
      <div *ngIf="canDisplayApmtTime()" class="appointment-info-box">
        {{
          getFullAppointment()?.time
            | parseZoneTimeString
              : timezoneService.getTimezone(getFullAppointment())
            | localeDate: 'fullDate'
            | async
            | titlecase
        }}
      </div>

      <div *ngIf="canDisplayApmtTime()" class="appointment-info-box">
        {{
          getFullAppointment()?.time
            | parseZoneTimeString
              : timezoneService.getTimezone(getFullAppointment())
            | localeDate: 'shortTime'
            | async
            | titlecase
        }}
      </div>

      <div *ngIf="this.updateTime" class="appointment-info-box">
        {{
          this.updateTime
            | parseZoneTimeString
              : timezoneService.getTimezone(getFullAppointment())
            | localeDate: 'fullDate'
            | async
            | titlecase
        }}
      </div>

      <div class="appointment-info-box">
        <sc-icon
          [name]="appointment?.outsideIntervention ? 'home' : 'fitter'"
        ></sc-icon>
        <span>{{
          (appointment?.outsideIntervention
            ? 'DeclarationEndHistory.atHome'
            : 'DeclarationEndHistory.inCenter'
          ) | translate
        }}</span>
      </div>

      <div
        class="appointment-info-box"
        *ngIf="appointment?.outsideIntervention"
      >
        {{ appointment?.interventionLocation?.address }}
      </div>

      <div
        class="appointment-info-box"
        *ngIf="appointment?.isReplacementVehicleSelected"
      >
        <sc-icon name="car"></sc-icon>
        <span>{{
          'DeclarationEndHistory.withReplacementVehicle' | translate
        }}</span>
      </div>

      <div class="appointment-info-box appointment-info-address" *ngIf="fitter">
        <p>
          {{ fitter | fitterName | async }}
        </p>
        <p
          *ngIf="fittersSB.isNotItinerant(fitter)"
          [innerHtml]="fittersSB.getCompleteAddress(fitter)"
        ></p>
      </div>
    </div>
  `,
})
export class DeclarationEndHistoryAppointmentComponent implements OnInit {
  @Input() appointment: Appointment | RequestAppointment;
  @Input() updateTime: string | Date;

  fitter: Fitter;

  constructor(
    public fittersSB: FittersSandboxService,
    public timezoneService: TimezoneService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.fittersSB
      .fetchFitter({
        id: this.appointment?.fitterId,
        brandId: this.appointment?.brandId,
      })
      .subscribe((fitter) => {
        this.fitter = fitter;
        this.changeDetectorRef.detectChanges();
      });
  }

  getTypeAppointmentClass(): string {
    return this.appointment?.outsideIntervention ? 'at-home' : 'in-center';
  }

  getFullAppointment() {
    return this.appointment as Appointment;
  }

  canDisplayApmtTime() {
    return (
      this.appointment && 'time' in this.appointment && this.appointment?.time
    );
  }
}
